import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  AppBar, 
  Box, 
  Toolbar, 
  Typography, 
  Button, 
  Container,
  useMediaQuery,
  useTheme,
  IconButton,
  Drawer,
  Paper,
  Stack,
  Grid,
  Collapse
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import DownloadIcon from '@mui/icons-material/Download';
import CodeIcon from '@mui/icons-material/Code';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import ReactMarkdown from 'react-markdown';

// Local imports
import { riverhackColors } from '../theme/riverhackTheme';
import { Event } from '../types/event';
import { generateIcsContent } from '../utils/ics';
import ScrollToTop from '../components/ScrollToTop';

// Assets
import riverhacksLogo from '../assets/logos/Riverhacks_Logo_RGB_Full Color_Stacked.png';
import riverhacksLogoWhite from '../assets/logos/riverhacks-logo-white-horizontal.png';
import heroBackground from '../assets/SwirlsGradient3.jpg';
import webAILogo from '../assets/logos/webAI-Black.png';
import serpAPILogo from '../assets/logos/serpapi.png';
import vercelLogo from '../assets/logos/vercel-logotype-light.png';
import cityOfAustinLogo from '../assets/logos/city-of-austin.png';

// Sections to navigate to
const sections = [
  { id: 'home', label: 'Home' },
  { id: 'main-theme', label: 'Main Theme' },
  { id: 'announcements', label: 'Announcements' },
  { id: 'schedule', label: 'Schedule' },
  { id: 'faq', label: 'FAQ' },
  { id: 'challenges', label: 'Challenges' },
  { id: 'webai', label: 'Sponsors' }
];

// RiverHacks dedicated navbar component
const RiverHacksNavbar = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [drawerOpen, setDrawerOpen] = useState(false);
  const navigate = useNavigate();

  const handleDrawerToggle = () => {
    setDrawerOpen(!drawerOpen);
  };

  const handleHomeClick = () => {
    navigate('/');
  };

  const scrollToSection = (sectionId: string) => {
    if (sectionId === 'home') {
      handleHomeClick();
      return;
    }
    const element = document.getElementById(sectionId);
    if (element) {
      const navbarHeight = 80; // Height of the navbar
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - navbarHeight;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
    if (isMobile) {
      setDrawerOpen(false);
    }
  };

  return (
    <>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          bgcolor: riverhackColors.sandBeige,
          borderBottom: `1px solid ${riverhackColors.deepPurple}20`,
          position: 'sticky',
          top: 0,
          zIndex: theme => theme.zIndex.drawer + 1,
        }}
      >
        <Container maxWidth="xl">
          <Toolbar
            disableGutters
            sx={{
              py: { xs: 2, md: 3 },
              px: { xs: 2, sm: 3, md: 4 },
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              minHeight: { xs: 64, md: 80 },
            }}
          >
            {/* Logo/Title */}
            <Box
              sx={{
                flexGrow: 1,
                display: 'flex',
                alignItems: 'center'
              }}
            >
              <img 
                src={riverhacksLogo} 
                alt="RiverHacks 2024 Logo" 
                style={{
                  height: '40px',
                  width: 'auto'
                }}
              />
            </Box>

            {/* Desktop navigation */}
            <Box sx={{ flexGrow: 1, display: { xs: 'none', md: 'flex' }, justifyContent: 'center' }}>
              {sections.map((section) => (
                <Button
                  key={section.id}
                  onClick={() => scrollToSection(section.id)}
                  sx={{ 
                    my: 2, 
                    mx: 1.5, 
                    color: riverhackColors.deepPurple,
                    fontSize: '1.1rem',
                    fontWeight: 'medium',
                    '&:hover': {
                      color: riverhackColors.vibrantOrange,
                    }
                  }}
                >
                  {section.label}
                </Button>
              ))}
            </Box>

            {/* Register button aligned to the right - only show on desktop */}
            <Box sx={{ display: { xs: 'none', md: 'block' } }}>
              <Button 
                component="a"
                href="https://lu.ma/event/evt-Zz93oWdkPvhbed5"
                target="_blank"
                rel="noopener noreferrer"
                variant="contained" 
                color="primary"
                className="luma-checkout--button"
                data-luma-action="checkout"
                data-luma-event-id="evt-Zz93oWdkPvhbed5"
                sx={{ 
                  borderRadius: '28px',
                  px: 3,
                  fontWeight: 'bold',
                  textDecoration: 'none',
                  bgcolor: riverhackColors.vibrantOrange,
                  '&:hover': {
                    bgcolor: riverhackColors.terracotta,
                  }
                }}
              >
                Register Now
              </Button>
            </Box>

            {/* Mobile menu button - moved to the right */}
            <Box sx={{ display: { xs: 'flex', md: 'none' }, ml: 2 }}>
              <IconButton
                size="large"
                aria-label="Open navigation menu"
                aria-expanded={drawerOpen}
                onClick={handleDrawerToggle}
                color="primary"
              >
                <MenuIcon />
              </IconButton>
            </Box>
          </Toolbar>
        </Container>
      </AppBar>

      {/* Mobile Drawer */}
      <Drawer
        variant="temporary"
        open={drawerOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': { 
            width: { xs: '100%', sm: 'max-content', minWidth: 320 },
            p: 3,
            background: theme.palette.mode === 'light' 
              ? 'rgba(255, 255, 255, 0.85)'
              : 'rgba(18, 18, 18, 0.85)',
            backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)',
            border: 'none',
            boxShadow: theme.palette.mode === 'light'
              ? '0 4px 30px rgba(0, 0, 0, 0.1)'
              : '0 4px 30px rgba(0, 0, 0, 0.3)',
            height: 'auto',
            maxHeight: '100%',
            top: 0,
            bottom: 'auto',
            borderRadius: { xs: '0 0 0 16px', sm: '16px 0 0 16px' },
          },
        }}
      >
        <Stack spacing={4} sx={{ alignItems: 'center' }}>
          {/* Drawer Header */}
          <Box sx={{ 
            width: '100%',
            display: 'flex', 
            flexDirection: 'column',
            alignItems: 'center',
            position: 'relative',
            mb: 2,
          }}>
            {/* Close button - positioned absolutely in the top-right corner */}
            <IconButton 
              onClick={handleDrawerToggle}
              aria-label="Close navigation menu"
              sx={{
                position: 'absolute',
                right: 0,
                top: 0,
              }}
            >
              <CloseIcon />
            </IconButton>
            
            {/* Centered Logo */}
            <Box sx={{ 
              height: 50,
              display: 'flex',
              justifyContent: 'center',
              width: '100%',
              mt: 2
            }}>
              <img 
                src={riverhacksLogo} 
                alt="RiverHacks 2024 Logo" 
                style={{
                  height: '100%',
                  width: 'auto'
                }}
              />
            </Box>
          </Box>

          {/* Navigation Links */}
          <Stack spacing={2} sx={{ width: '100%', alignItems: 'center' }}>
            {sections.map((section) => (
              <Button
                key={section.id}
                onClick={() => scrollToSection(section.id)}
                fullWidth
                sx={{
                  justifyContent: 'center',
                  color: riverhackColors.deepPurple,
                  fontSize: '1.125rem',
                  fontWeight: 600,
                  py: 1.5,
                  borderRadius: 1,
                  '&:hover': {
                    bgcolor: `${riverhackColors.deepPurple}10`,
                  },
                  '&:active': {
                    bgcolor: `${riverhackColors.deepPurple}20`,
                  },
                }}
              >
                {section.label}
              </Button>
            ))}

            {/* Register Button */}
            <Button 
              component="a"
              href="https://lu.ma/event/evt-Zz93oWdkPvhbed5"
              target="_blank"
              rel="noopener noreferrer"
              variant="contained" 
              color="primary"
              className="luma-checkout--button"
              data-luma-action="checkout"
              data-luma-event-id="evt-Zz93oWdkPvhbed5"
              fullWidth
              sx={{ 
                mt: 2,
                borderRadius: '28px',
                py: 1.5,
                fontWeight: 'bold',
                textDecoration: 'none',
                bgcolor: riverhackColors.vibrantOrange,
                '&:hover': {
                  bgcolor: riverhackColors.terracotta,
                }
              }}
            >
              Register Now
            </Button>
          </Stack>
        </Stack>
      </Drawer>
    </>
  );
};

// Section component for creating each main section
const Section = ({ 
  id, 
  title, 
  children, 
  fullWidth = false
}: { 
  id: string, 
  title: string, 
  children: React.ReactNode,
  fullWidth?: boolean 
}) => (
  <Box 
    id={id} 
    component="section"
    sx={{ 
      py: fullWidth ? 0 : 10,
      minHeight: fullWidth ? '100vh' : '80vh',
      '&:nth-of-type(even)': {
        bgcolor: `${riverhackColors.sandBeige}30`
      }
    }}
  >
    {fullWidth ? children : (
      <Container maxWidth="lg">
        <Typography 
          variant="h3" 
          component="h2" 
          align="center" 
          gutterBottom 
          sx={{ 
            fontWeight: 'bold',
            mb: 6,
            color: riverhackColors.deepPurple
          }}
        >
          {title}
        </Typography>
        {children}
      </Container>
    )}
  </Box>
);

// Add this type for the Luma global object
declare global {
  interface Window {
    LumaCheckout?: any;
  }
}

// Add this type definition
interface Announcement {
  id: string;
  body: string;
  published: Date;
}

const announcements: Announcement[] = [
  {
    id: '1',
    body: '**Welcome to RiverHacks 2025!** Registration is now open. Sign up early to secure your spot!',
    published: new Date('2025-03-16T10:00:00'),
  }
];

// Update the schedule data name
const hackathonSchedule: Event[] = [
  // Saturday Events
  {
    id: 'sat-1',
    name: 'Check-In',
    description: 'Get your badge, swag bag, and meet fellow hackers',
    location: 'CGCS Main Lobby',
    start: new Date('2025-04-26T09:00:00'),
    end: new Date('2025-04-26T10:30:00'),
    type: 'Main Events'
  },
  {
    id: 'sat-2',
    name: 'Team Formations',
    description: 'Find your teammates and start brainstorming',
    location: 'Main Hall',
    start: new Date('2025-04-26T10:30:00'),
    end: new Date('2025-04-26T11:00:00'),
    type: 'Main Events'
  },
  {
    id: 'sat-3',
    name: 'Opening Ceremony',
    description: 'Welcome speeches, rules overview, and event kickoff',
    location: 'Main Hall',
    start: new Date('2025-04-26T11:00:00'),
    end: new Date('2025-04-26T12:00:00'),
    type: 'Ceremonies'
  },
  {
    id: 'sat-4',
    name: 'Hacking Begins',
    description: 'Start working on your projects',
    location: 'Hacking Spaces',
    start: new Date('2025-04-26T12:00:00'),
    end: new Date('2025-04-26T23:59:59'),
    type: 'Hacking'
  },
  {
    id: 'sat-5',
    name: 'Lunch',
    description: 'Take a break and enjoy lunch with fellow hackers',
    location: 'Dining Area',
    start: new Date('2025-04-26T14:00:00'),
    end: new Date('2025-04-26T15:00:00'),
    type: 'Food'
  },
  {
    id: 'sat-6',
    name: 'Dinner',
    description: 'Evening meal break',
    location: 'Dining Area',
    start: new Date('2025-04-26T19:00:00'),
    end: new Date('2025-04-26T20:00:00'),
    type: 'Food'
  },
  // Sunday Events
  {
    id: 'sun-1',
    name: 'Breakfast',
    description: 'Start your day with breakfast',
    location: 'Dining Area',
    start: new Date('2025-04-27T07:00:00'),
    end: new Date('2025-04-27T08:00:00'),
    type: 'Food'
  },
  {
    id: 'sun-2',
    name: 'Projects Due',
    description: 'Final submission deadline - all projects must be submitted',
    location: 'Hacking Spaces',
    start: new Date('2025-04-27T12:00:00'),
    end: new Date('2025-04-27T12:00:00'),
    type: 'Main Events'
  },
  {
    id: 'sun-4',
    name: 'Judging',
    description: 'Present your projects to the judges',
    location: 'Main Hall',
    start: new Date('2025-04-27T13:00:00'),
    end: new Date('2025-04-27T16:00:00'),
    type: 'Main Events'
  },
  {
    id: 'sun-5',
    name: 'Closing Ceremony',
    description: 'Awards presentation and event wrap-up',
    location: 'Main Hall',
    start: new Date('2025-04-27T16:00:00'),
    end: new Date('2025-04-27T17:00:00'),
    type: 'Ceremonies'
  }
];

// Update the ScheduleSection component to use the new name
const ScheduleSection = () => {
  const [selectedFilter, setSelectedFilter] = useState<string | null>(null);

  // Update to use hackathonSchedule
  const filters = Array.from(new Set(hackathonSchedule.map(event => event.type)));

  const groupedEvents = hackathonSchedule.reduce((acc, event) => {
    // Format the date as "Day, Month Date"
    const day = event.start.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
    if (!acc[day]) {
      acc[day] = [];
    }
    acc[day].push(event);
    return acc;
  }, {} as Record<string, Event[]>);

  return (
    <Box sx={{ width: '100%' }}>
      {/* Filters */}
      <Paper 
        elevation={0}
        sx={{ 
          p: 2, 
          mb: 4,
          bgcolor: 'background.paper',
          border: `1px solid ${riverhackColors.deepPurple}20`,
          maxWidth: '1000px',  // Add max width
          mx: 'auto'  // Center the filters
        }}
      >
        <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
          <Stack direction="row" spacing={1} flexWrap="wrap">
            {filters.map((filter) => (
              <Button
                key={filter}
                variant={selectedFilter === filter ? "contained" : "outlined"}
                onClick={() => setSelectedFilter(selectedFilter === filter ? null : filter)}
                size="small"
                sx={{
                  borderRadius: '16px',
                  textTransform: 'none'
                }}
              >
                {filter}
              </Button>
            ))}
          </Stack>
          <Button
            startIcon={<CalendarMonthIcon />}
            variant="outlined"
            onClick={() => {
              const icsContent = generateIcsContent(hackathonSchedule);
              const blob = new Blob([icsContent], { type: 'text/calendar' });
              const url = window.URL.createObjectURL(blob);
              const link = document.createElement('a');
              link.href = url;
              link.setAttribute('download', 'riverhacks-schedule.ics');
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
            }}
            sx={{
              borderRadius: '16px',
            }}
          >
            Add to Calendar
          </Button>
        </Stack>
      </Paper>

      {/* Schedule Grid */}
      <Box 
        sx={{ 
          maxWidth: '1000px',  // Match filter paper width
          mx: 'auto'  // Center the grid
        }}
      >
        <Grid container spacing={4}>
          {Object.entries(groupedEvents).map(([day, events]) => (
            <Grid item xs={12} md={6} key={day}>
              <Typography
                variant="h5"
                sx={{
                  mb: 2,
                  color: riverhackColors.deepPurple,
                  fontWeight: 'bold'
                }}
              >
                {day}
              </Typography>
              <Stack spacing={2}>
                {events
                  .filter(event => !selectedFilter || event.type === selectedFilter)
                  .map((event) => (
                    <Paper
                      key={event.id}
                      elevation={0}
                      sx={{
                        p: 2,
                        border: `1px solid ${riverhackColors.deepPurple}20`,
                        borderLeft: `4px solid ${riverhackColors.vibrantOrange}`,
                        '&:hover': {
                          bgcolor: `${riverhackColors.sandBeige}30`,
                        }
                      }}
                    >
                      <Stack spacing={1}>
                        <Typography variant="h6" sx={{ color: riverhackColors.deepPurple }}>
                          {event.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {event.start.toLocaleTimeString([], { 
                            hour: '2-digit', 
                            minute: '2-digit'
                          })} - {event.end.toLocaleTimeString([], { 
                            hour: '2-digit', 
                            minute: '2-digit'
                          })}
                        </Typography>
                        <Typography variant="body2" sx={{ color: riverhackColors.terracotta }}>
                          📍 {event.location}
                        </Typography>
                        <Typography variant="body2">
                          {event.description}
                        </Typography>
                      </Stack>
                    </Paper>
                  ))}
              </Stack>
            </Grid>
          ))}
        </Grid>
      </Box>
    </Box>
  );
};

// Add this interface before the faqList definition
interface FAQ {
  isActive: boolean;
  question: string;
  answer: string;
}

// Update the faqList type annotation
const faqList: FAQ[] = [
  {
    isActive: true,
    question: "Rules and Code of Conduct",
    answer: "All work must be done at the event—no sneaky pre-built projects allowed. If you didn't build it here, you can't demo it. All attendees (hackers, supporters, mentors, volunteers, etc.) must follow our code of conduct. Play fair, be respectful, and keep it fun."
  },
  {
    isActive: false,
    question: "How will teams work?",
    answer: "Teams can have up to 4 members. No team? No problem! Meeting new people and collaborating is half the fun. We'll have team-matching available on the day of the event, but if you already have a crew, you're all set."
  },
  {
    isActive: false,
    question: "What should I bring?",
    answer: "A valid university ID or proof that you're over 18, a laptop, chargers, and whatever else you need to survive 24 hours of coding. Weapons, alcohol, illegal substances, and power tools? Hard no. But bring your best ideas, good vibes, and an unlimited supply of high-fives."
  },
  {
    isActive: false,
    question: "Travel Reimbursements",
    answer: "Unfortunately, we can't cover travel costs this year, but we highly recommend budget-friendly options like Uber, Lyft, CapMetro, or, if you're feeling adventurous, Lime/Bird scooters. Consider it part of the hackathon challenge—how to get here efficiently!"
  },
  {
    isActive: false,
    question: "Will there be prizes?",
    answer: "Absolutely. We'll be giving away prizes for the top projects, mini-challenges, and maybe even some surprise categories. Full details will be revealed at the Opening Ceremony, so bring your A-game."
  },
  {
    isActive: false,
    question: "How much will this cost me?",
    answer: "Nothing. Zero. Zilch. It's completely free. We'll cover your Wi-Fi, meals, workspace, swag, and, of course, eternal bragging rights."
  },
  {
    isActive: false,
    question: "Where will RiverHacks take place?",
    answer: "RiverHacks will be hosted at the Center for Government and Civic Service (CGCS).\nAddress: 1218 West Ave, Austin, TX 78701\nEverything—hacking, judging, and mini-events—will happen in person, so get ready for an action-packed weekend."
  },
  {
    isActive: false,
    question: "When will RiverHacks take place?",
    answer: "Check-in starts Saturday, April 26 at 9:00 AM, and the hackathon officially ends Sunday, April 27 at 3:00 PM. That's 29 hours of non-stop creativity, coding, and possibly too much caffeine."
  },
  {
    isActive: false,
    question: "Will parking be provided?",
    answer: "Yes! Free parking is available at CGCS.\nParking Garage: 816 W 12th St, Austin, TX 78701\nHeads up—the garage locks up at 10:00 PM on Saturday, so plan accordingly. It reopens at 7:00 AM on Sunday, so don't get stuck unless you're planning to code through the night (which is totally encouraged)."
  },
  {
    isActive: false,
    question: "What if I missed the application deadline?",
    answer: "No worries! We'll have walk-in registration on Saturday, April 26 at 9:00 AM. Just show up, sign in, and get hacking."
  },
  {
    isActive: false,
    question: "I have more questions. Who do I ask?",
    answer: "We've got answers! Shoot us an email at "
      + "contact@austincs.org".link("mailto:contact@austincs.org")
      + ", and we'll be happy to help. Or, you know, just show up and ask us in person—we're friendly."
  }
];

// Update the FaqItem component props type
const FaqItem = ({ faq }: { faq: FAQ }) => {
  const [isOpen, setIsOpen] = useState(faq.isActive);

  return (
    <Paper
      elevation={0}
      sx={{
        mt: 3,
        overflow: 'hidden',
        border: `1px solid ${riverhackColors.deepPurple}20`,
        borderRadius: 2,
        transition: 'all 0.3s ease',
        '&:hover': {
          borderColor: riverhackColors.vibrantOrange,
        }
      }}
    >
      <Button
        onClick={() => setIsOpen(!isOpen)}
        fullWidth
        sx={{
          justifyContent: 'space-between',
          p: 3,
          color: riverhackColors.deepPurple,
          textTransform: 'none',
          fontSize: '1.1rem',
          fontWeight: 'medium',
          '&:hover': {
            backgroundColor: `${riverhackColors.sandBeige}30`
          }
        }}
      >
        <Typography 
          sx={{ 
            textAlign: 'left',
            fontWeight: isOpen ? 'bold' : 'medium',
            color: isOpen ? riverhackColors.vibrantOrange : riverhackColors.deepPurple
          }}
        >
          {faq.question}
        </Typography>
        {isOpen ? <RemoveIcon /> : <AddIcon />}
      </Button>
      <Collapse in={isOpen}>
        <Box
          sx={{
            p: 3,
            bgcolor: 'background.paper',
            borderTop: `1px solid ${riverhackColors.deepPurple}20`
          }}
        >
          <Typography
            variant="body1"
            dangerouslySetInnerHTML={{ __html: faq.answer }}
            sx={{
              color: 'text.secondary',
              lineHeight: 1.6,
              whiteSpace: 'pre-line',
              '& a': {
                color: riverhackColors.vibrantOrange,
                textDecoration: 'none',
                fontWeight: 'medium',
                '&:hover': {
                  textDecoration: 'underline'
                }
              }
            }}
          />
        </Box>
      </Collapse>
    </Paper>
  );
};

// Update the FAQ section component
const FaqSection = () => {
  return (
    <Box sx={{ width: '100%', maxWidth: '1200px', mx: 'auto' }}>
      <Typography
        variant="body1"
        align="center"
        sx={{
          mb: 6,
          maxWidth: '800px',
          mx: 'auto',
          color: riverhackColors.deepPurple,
          fontSize: '1.1rem',
          lineHeight: 1.6
        }}
      >
        Got questions? We've got answers! Check out our frequently asked questions below.
      </Typography>

      <Grid container spacing={4}>
        <Grid xs={12} md={6}>
          {faqList.slice(0, Math.ceil(faqList.length / 2)).map((faq, i) => (
            <FaqItem key={i} faq={faq} />
          ))}
        </Grid>
        <Grid xs={12} md={6}>
          {faqList.slice(Math.ceil(faqList.length / 2)).map((faq, i) => (
            <FaqItem key={i} faq={faq} />
          ))}
        </Grid>
      </Grid>
    </Box>
  );
};

// Add these interfaces near the top of the file with other interfaces
interface Sponsor {
  name: string;
  logo: string;
  website: string;
}

interface SponsorSection {
  title: string;
  sponsors: Sponsor[];
}

// Update the sponsor data
const titleSponsor: Sponsor = {
  name: "WebAI",
  logo: webAILogo,
  website: "https://webai.com",
};

const sponsorSections: SponsorSection[] = [
  {
    title: "Sporting Sponsors",
    sponsors: [
      {
        name: "SerpAPI",
        logo: serpAPILogo,
        website: "https://serpapi.com",
      },
      {
        name: "Vercel",
        logo: vercelLogo,
        website: "https://vercel.com",
      }
    ],
  },
  {
    title: "Local Startups Support",
    sponsors: [
      { 
        name: "Loop Message", 
        logo: "https://loopmessage.com/files/logo.svg", 
        website: "https://loopmessage.com" 
      }
    ]
  }
];

// Update the SponsorLogo component to center align logos
const SponsorLogo = ({ sponsor, className }: { sponsor: Sponsor; className?: any }) => {
  return (
    <Box
      component="a"
      href={sponsor.website}
      target="_blank"
      rel="noopener noreferrer"
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 4,
        bgcolor: 'background.paper',
        borderRadius: 2,
        transition: 'transform 0.2s',
        border: `1px solid ${riverhackColors.deepPurple}20`,
        '&:hover': {
          transform: 'scale(1.05)',
          borderColor: riverhackColors.vibrantOrange,
        },
        ...(className || {})
      }}
    >
      <Box
        component="img"
        src={sponsor.logo}
        alt={`${sponsor.name} logo`}
        sx={{
          maxWidth: '100%',
          height: 'auto',
          objectFit: 'contain',
          width: sponsor.name === "WebAI" ? '240px' : '160px',
          margin: '0 auto', // Center the image
          display: 'block' // Ensure proper margin centering
        }}
      />
    </Box>
  );
};

// Update the SponsorsSection component to center the SerpAPI logo
const SponsorsSection = () => {
  return (
    <Box sx={{ maxWidth: '1200px', mx: 'auto' }}>
      {/* Title Sponsor */}
      <Box sx={{ mb: 8 }}>
        <Typography
          variant="h4"
          align="center"
          sx={{
            color: riverhackColors.deepPurple,
            mb: 4,
            fontWeight: 'bold'
          }}
        >
          Title Sponsor
        </Typography>
        <SponsorLogo 
          sponsor={titleSponsor} 
          className={{
            maxWidth: '600px',
            mx: 'auto'
          }} 
        />
      </Box>

      {/* Other Sponsor Sections */}
      {sponsorSections.map((section, index) => (
        <Box
          key={section.title}
          sx={{
            mb: 8,
            pb: index !== sponsorSections.length - 1 ? 8 : 0,
            borderBottom: index !== sponsorSections.length - 1 ? `1px solid ${riverhackColors.deepPurple}20` : 'none'
          }}
        >
          <Typography
            variant="h4"
            align="center"
            sx={{
              color: riverhackColors.deepPurple,
              mb: 4,
              fontWeight: 'bold'
            }}
          >
            {section.title}
          </Typography>
          <Grid 
            container 
            spacing={4} 
            alignItems="center"
            justifyContent="center" // Center the grid items
          >
            {section.sponsors.map((sponsor) => (
              <Grid 
                item 
                xs={12} 
                md={4} 
                key={sponsor.name}
                sx={{
                  display: 'flex',
                  justifyContent: 'center' // Center the sponsor logo
                }}
              >
                <SponsorLogo sponsor={sponsor} />
              </Grid>
            ))}
          </Grid>
        </Box>
      ))}

      {/* Sponsorship CTA */}
      <Box 
        sx={{ 
          mt: 8, 
          textAlign: 'center',
          p: 4,
          bgcolor: `${riverhackColors.sandBeige}30`,
          borderRadius: 2
        }}
      >
        <Typography 
          variant="h5"
          sx={{ 
            mb: 2,
            color: riverhackColors.deepPurple,
            fontWeight: 'medium'
          }}
        >
          Interested in becoming a sponsor?
        </Typography>
        <Typography 
          variant="body1"
          sx={{ 
            fontSize: '1.1rem',
            '& a': {
              color: riverhackColors.vibrantOrange,
              textDecoration: 'none',
              fontWeight: 'bold',
              '&:hover': {
                textDecoration: 'underline'
              }
            }
          }}
        >
          Reach out to us at{' '}
          <a href="mailto:contact@austincs.org">contact@austincs.org</a>
        </Typography>
      </Box>
    </Box>
  );
};

const RiverHacks = () => {  
  return (
    <Box sx={{ 
      bgcolor: 'background.default',
      color: riverhackColors.deepPurple
    }}>
      {/* Navbar */}
      <RiverHacksNavbar />

      {/* Content sections */}
      <Box>
        {/* Home/Hero Section */}
        <Section id="home" title="RiverHacks 2024" fullWidth>
          <Box
            sx={{
              position: 'relative',
              width: '100%',
              height: '100vh',
              backgroundImage: `url(${heroBackground})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              color: 'white',
              m: 0,
              p: 0,
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.3)',
                zIndex: 1
              }
            }}
          >
            <Container 
              maxWidth="lg" 
              sx={{ 
                position: 'relative', 
                zIndex: 2,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                pt: { xs: 8, md: 0 }
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  mb: 6
                }}
              >
                <img
                  src={riverhacksLogoWhite}
                  alt="RiverHacks 2024"
                  style={{
                    maxWidth: '900px',
                    width: '100%',
                    height: 'auto'
                  }}
                />
              </Box>
              
              <Typography 
                variant="h5" 
                align="center" 
                paragraph
                sx={{ 
                  color: 'white',
                  textShadow: '1px 1px 2px rgba(0,0,0,0.3)',
                  fontSize: { 
                    xs: '1.5rem',
                    sm: '1.8rem',
                    md: '3rem'
                  },
                  fontWeight: 'bold'
                }}
              >
                Austin Community College's Premier Hackathon
              </Typography>
              
              <Typography 
                variant="body1" 
                align="center" 
                paragraph
                sx={{ 
                  color: 'white',
                  textShadow: '1px 1px 2px rgba(0,0,0,0.3)',
                  maxWidth: '800px',
                  mx: 'auto',
                  fontSize: { 
                    xs: '1rem',
                    sm: '1.2rem',
                    md: '1.2rem'
                  }
                }}
              >
                Join us for 32 hours of coding, creativity, and collaboration. Build innovative solutions, win amazing prizes, and connect with industry professionals.
              </Typography>
              
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                <Button 
                  component="a"
                  href="https://lu.ma/event/evt-Zz93oWdkPvhbed5"
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="contained" 
                  color="primary"
                  className="luma-checkout--button"
                  data-luma-action="checkout"
                  data-luma-event-id="evt-Zz93oWdkPvhbed5"
                  size="large"
                  sx={{ 
                    borderRadius: '28px',
                    px: 4,
                    py: 1.5,
                    fontWeight: 'bold',
                    fontSize: '1.1rem',
                    textDecoration: 'none',
                    bgcolor: riverhackColors.vibrantOrange,
                    '&:hover': {
                      bgcolor: riverhackColors.terracotta,
                    }
                  }}
                >
                  Register Now
                </Button>
              </Box>
            </Container>
          </Box>
        </Section>

        {/* Main Theme Section */}
        <Section id="main-theme" title="Public Safety & Accessibility">
          <Box sx={{ maxWidth: '1000px', mx: 'auto' }}>
            <Box sx={{ mb: 6, textAlign: 'center' }}>
              <Box
                component="img"
                src={cityOfAustinLogo}
                alt="City of Austin Logo"
                sx={{
                  width: '200px',
                  height: 'auto',
                  mb: 4
                }}
              />
              <Typography
                variant="h4"
                sx={{
                  color: riverhackColors.deepPurple,
                  fontWeight: 'bold',
                  mb: 2
                }}
              >
                RiverHacks Hackathon Challenge
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontSize: '1.1rem',
                  lineHeight: 1.6,
                  color: riverhackColors.deepPurple,
                  mb: 6
                }}
              >
                Join us in leveraging technology to create innovative solutions that enhance public safety and accessibility for all Austin residents. This hackathon theme challenges participants to develop tools, applications, and systems that support the City of Austin's strategic vision for a safer, more equitable, and more accessible community.
              </Typography>
            </Box>

            <Grid container spacing={4}>
              <Grid item xs={12}>
                <Typography
                  variant="h5"
                  sx={{
                    color: riverhackColors.deepPurple,
                    fontWeight: 'bold',
                    mb: 3
                  }}
                >
                  Challenge Areas
                </Typography>
                <Grid container spacing={3}>
                  {[
                    {
                      title: 'Emergency Preparedness & Response',
                      description: 'Design solutions that help communities prepare for, respond to, and recover from natural disasters and emergencies with a focus on equitable access to resources'
                    },
                    {
                      title: 'Smart Infrastructure',
                      description: 'Develop applications that enhance the safety and accessibility of Austin\'s public spaces, transportation systems, and critical infrastructure'
                    },
                    {
                      title: 'Community Safety Engagement',
                      description: 'Create platforms that strengthen connections between public safety services and community members, promoting shared responsibility and trust'
                    },
                    {
                      title: 'Inclusive Accessibility',
                      description: 'Build tools that ensure all residents, including those with disabilities and those from historically underserved communities, can access vital safety information and services'
                    }
                  ].map((area, index) => (
                    <Grid item xs={12} md={6} key={index}>
                      <Paper
                        elevation={0}
                        sx={{
                          p: 3,
                          height: '100%',
                          border: `1px solid ${riverhackColors.deepPurple}20`,
                          borderLeft: `4px solid ${riverhackColors.vibrantOrange}`,
                          '&:hover': {
                            bgcolor: `${riverhackColors.sandBeige}30`,
                          }
                        }}
                      >
                        <Typography
                          variant="h6"
                          sx={{
                            color: riverhackColors.deepPurple,
                            fontWeight: 'bold',
                            mb: 1
                          }}
                        >
                          {area.title}
                        </Typography>
                        <Typography variant="body1">
                          {area.description}
                        </Typography>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              </Grid>

              <Grid item xs={12}>
                <Typography
                  variant="h5"
                  sx={{
                    color: riverhackColors.deepPurple,
                    fontWeight: 'bold',
                    mb: 2
                  }}
                >
                  Real-World Impact
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    fontSize: '1.1rem',
                    lineHeight: 1.6,
                    color: riverhackColors.deepPurple
                  }}
                >
                  Your innovations have the potential to directly impact how Austin approaches public safety and accessibility challenges. Winning solutions may have the opportunity to be implemented or further developed in partnership with the City of Austin, contributing to strategic goals outlined in the Citywide Strategic Plan.
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    fontSize: '1.1rem',
                    lineHeight: 1.6,
                    fontStyle: 'italic',
                    color: riverhackColors.deepPurple,
                    mt: 2
                  }}
                >
                  This hackathon theme is presented in partnership with the City of Austin and aligns with the Public Safety and Mobility & Critical Infrastructure priorities outlined in Austin's Citywide Strategic Plan.
                </Typography>
              </Grid>
            </Grid>
          </Box>
        </Section>

        {/* Announcements Section */}
        <Section id="announcements" title="Announcements">
          <Stack spacing={3} sx={{ maxWidth: '800px', mx: 'auto' }}>
            {announcements.length > 0 ? (
              announcements.map((announcement) => (
                <Paper
                  key={announcement.id}
                  elevation={0}
                  sx={{
                    position: 'relative',
                    p: 4,
                    bgcolor: 'white',
                    border: `8px solid ${riverhackColors.deepPurple}`,
                    boxShadow: `15px 15px 0 ${riverhackColors.deepPurple}`,
                    transform: 'rotate(-2deg)',
                    transition: 'all 0.3s ease',
                    overflow: 'hidden',
                    '&:hover': {
                      transform: 'rotate(0deg) scale(1.02)',
                      boxShadow: `20px 20px 0 ${riverhackColors.deepPurple}`,
                    }
                  }}
                >
                  {/* Corner Ribbon */}
                  <Box
                    sx={{
                      position: 'absolute',
                      top: -50,
                      right: -50,
                      height: '100px',
                      width: '100px',
                      bgcolor: riverhackColors.vibrantOrange,
                      transform: 'rotate(45deg)',
                    }}
                  >
                    <Typography
                      sx={{
                        position: 'absolute',
                        bottom: '-5px',
                        left: '0',
                        width: '100%',
                        color: 'white',
                        fontWeight: 'bold',
                        textTransform: 'uppercase',
                        fontSize: '14px',
                        letterSpacing: '1px',
                        textAlign: 'center',
                      }}
                    >
                      New Update
                    </Typography>
                  </Box>

                  <Typography 
                    component="span" 
                    sx={{ 
                      display: 'block',
                      fontSize: '24px',
                      fontWeight: 700,
                      color: riverhackColors.deepPurple,
                      textTransform: 'uppercase',
                      mb: 1,
                      borderBottom: `2px solid ${riverhackColors.deepPurple}`,
                      width: '50%'
                    }}
                  >
                    {announcement.published.toLocaleDateString('en-us', {
                      month: 'long',
                      day: 'numeric',
                      year: 'numeric'
                    })}
                  </Typography>

                  <Typography 
                    component="div" 
                    sx={{ 
                      color: riverhackColors.deepPurple,
                      fontSize: '16px',
                      lineHeight: 1.4,
                      mb: 2,
                      '& p': { mt: 1 },
                      '& strong': { 
                        color: riverhackColors.vibrantOrange,
                        fontWeight: 'bold'
                      }
                    }}
                  >
                    <ReactMarkdown>{announcement.body}</ReactMarkdown>
                  </Typography>
                </Paper>
              ))
            ) : (
              <Typography 
                align="center" 
                sx={{ 
                  color: riverhackColors.deepPurple,
                  fontSize: '1.2rem',
                  my: 4 
                }}
              >
                There are no announcements at this time.
              </Typography>
            )}
          </Stack>
        </Section>

        {/* Schedule Section */}
        <Section id="schedule" title="Schedule">
          <ScheduleSection />
        </Section>

        {/* FAQ Section */}
        <Section id="faq" title="FAQ">
          <FaqSection />
        </Section>

        {/* Challenges Section */}
        <Section id="challenges" title="Challenges">
          <Box sx={{ maxWidth: '800px', mx: 'auto' }}>
            <Typography 
              variant="body1" 
              sx={{ 
                mb: 4,
                color: riverhackColors.deepPurple,
                fontSize: '1.1rem',
                lineHeight: 1.6,
                textAlign: 'center'
              }}
            >
              This year, RiverHacks 2025 is all about civic innovation! While the full list of challenges is still being fine-tuned, expect projects that make a real-world impact in areas like public services, community engagement, sustainability, and more.
            </Typography>

            <Typography 
              variant="h5" 
              sx={{ 
                mb: 3,
                color: riverhackColors.deepPurple,
                fontWeight: 'bold'
              }}
            >
              What can you expect?
            </Typography>

            <Stack spacing={2} sx={{ mb: 4 }}>
              {[
                'Challenges designed to solve real civic and community-focused problems',
                'Opportunities to create tech solutions that benefit society',
                'Prizes for creativity, impact, and technical excellence'
              ].map((item, index) => (
                <Paper
                  key={index}
                  elevation={0}
                  sx={{
                    p: 2,
                    border: `1px solid ${riverhackColors.deepPurple}20`,
                    borderLeft: `4px solid ${riverhackColors.vibrantOrange}`,
                    '&:hover': {
                      bgcolor: `${riverhackColors.sandBeige}30`,
                    },
                    transition: 'all 0.3s ease'
                  }}
                >
                  <Typography variant="body1" sx={{ color: riverhackColors.deepPurple }}>
                    {item}
                  </Typography>
                </Paper>
              ))}
            </Stack>

            <Typography 
              variant="body1" 
              sx={{ 
                color: riverhackColors.deepPurple,
                fontSize: '1.1rem',
                lineHeight: 1.6,
                textAlign: 'center',
                fontStyle: 'italic'
              }}
            >
              Full challenge details will be announced closer to the event and officially revealed during the Opening Ceremony. Get ready to code with a purpose!
            </Typography>
          </Box>
        </Section>

        {/* WebAI Section */}
        <Section id="webai" title="Meet Our Title Sponsor: webAI">
          <Box sx={{ maxWidth: '800px', mx: 'auto' }}>
            <Box sx={{ mb: 6, textAlign: 'center' }}>
              <Box
                component="img"
                src={webAILogo}
                alt="WebAI Logo"
                sx={{
                  width: '300px',
                  height: 'auto',
                  mb: 4
                }}
              />
            </Box>

            <Typography 
              variant="body1" 
              sx={{ 
                mb: 4,
                color: riverhackColors.deepPurple,
                fontSize: '1.1rem',
                lineHeight: 1.6,
                textAlign: 'center'
              }}
            >
              webAI is a private, end to end AI solution that empowers businesses to create, distribute, and own the specialized intelligence employees need to do their best work. The platform runs locally—no cloud required—to guarantee security, efficiency, and complete data control, so enterprises can truly make AI their own.
            </Typography>

            <Typography 
              variant="body1" 
              sx={{ 
                mb: 6,
                color: riverhackColors.deepPurple,
                fontSize: '1.1rem',
                lineHeight: 1.6,
                textAlign: 'center',
                fontWeight: 'medium'
              }}
            >
              As our title sponsor for RiverHacks 2025, WebAI is providing exclusive early access to their developer tools for all participants. Explore their resources below!
            </Typography>

            <Stack spacing={2} sx={{ maxWidth: '600px', mx: 'auto' }}>
              <Button
                variant="contained"
                fullWidth
                href="https://webai.com/download"
                target="_blank"
                rel="noopener noreferrer"
                startIcon={<DownloadIcon />}
                sx={{
                  py: 2,
                  bgcolor: riverhackColors.vibrantOrange,
                  borderRadius: 2,
                  fontSize: '1.1rem',
                  fontWeight: 'bold',
                  '&:hover': {
                    bgcolor: riverhackColors.terracotta,
                  }
                }}
              >
                Download WebAI's Navigator Platform
              </Button>

              <Button
                variant="outlined"
                fullWidth
                href="https://github.com/thewebAI/webai-cli-public"
                target="_blank"
                rel="noopener noreferrer"
                startIcon={<CodeIcon />}
                sx={{
                  py: 2,
                  borderColor: riverhackColors.deepPurple,
                  color: riverhackColors.deepPurple,
                  borderRadius: 2,
                  fontSize: '1.1rem',
                  fontWeight: 'bold',
                  '&:hover': {
                    borderColor: riverhackColors.vibrantOrange,
                    color: riverhackColors.vibrantOrange,
                    bgcolor: 'transparent',
                  }
                }}
              >
                WebAI's Public Developer Entrypoint
              </Button>

              <Button
                variant="outlined"
                fullWidth
                href="https://support.webai.com/hc/en-us"
                target="_blank"
                rel="noopener noreferrer"
                startIcon={<MenuBookIcon />}
                sx={{
                  py: 2,
                  borderColor: riverhackColors.deepPurple,
                  color: riverhackColors.deepPurple,
                  borderRadius: 2,
                  fontSize: '1.1rem',
                  fontWeight: 'bold',
                  '&:hover': {
                    borderColor: riverhackColors.vibrantOrange,
                    color: riverhackColors.vibrantOrange,
                    bgcolor: 'transparent',
                  }
                }}
              >
                WebAI QuickStart Guide
              </Button>
            </Stack>
          </Box>
        </Section>

        {/* Sponsors Section */}
        <Section id="sponsors" title="Sponsors">
          <SponsorsSection />
        </Section>
      </Box>

      {/* Add ScrollToTop button */}
      <ScrollToTop />
    </Box>
  );
};

export default RiverHacks;
